import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import ModalContext from 'contexts/ModalContext'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineEnvelopeAltIcon from 'LuxKit/Icons/line/LineEnvelopeAltIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMobileAndroidIcon from 'LuxKit/Icons/line/LineMobileAndroidIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineRoomUpgradeIcon from 'LuxKit/Icons/line/LineRoomUpgradeIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import PaletteSheet from 'LuxKit/PaletteSheet'
import TextLink from 'LuxKit/TextLink'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import InfiniteConciergeDetailsModal from 'loyalty/components/InfiniteConciergeDetailsModal'
import LoyaltyInfiniteBrandLogo from 'loyalty/components/LoyaltyInfiniteBrandLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import {
  formatLoyaltyEsimInternationalDataHeading,
  getLoyaltyEsimGbAmount,
} from 'loyalty/utils/loyaltyBenefitLabels'
import { rem } from 'polished'
import * as React from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import focusStyle from 'styles/utils/focusStyle'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  &:focus-visible {
    ${focusStyle}
  }
`

const InfiniteBenefitList = styled.div`
  display: flex;
  flex-direction: column;
`

const InfiniteBenefitItem = styled.div`
  display: flex;
  align-items: start;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.product.loyalty.infinite.normalContrast};

  svg {
    flex-shrink: 0;
    height: ${rem(16)};
    width: ${rem(16)};
  }
`

const InfiniteBenefitDivider = styled(Divider)`
  margin: ${rem(8)} 0;
  opacity: 0.5;
`

const InfiniteConciergeCard = styled.div`
  --concierge-card-border-color: ${props => props.theme.palette.neutral.default.four};

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)};
  border: 1px solid color-mix(in srgb, var(--concierge-card-border-color) 50%, transparent);
  border-radius: ${props => props.theme.borderRadius.L};
`

const InfiniteAssistChip = styled(AssistChip)`
  --chip-stroke-color: ${props => props.theme.palette.neutral.default.four};
  color: ${props => props.theme.palette.neutral.default.one};

  &.variant-outline:not(:disabled) {
    --chip-stroke-color: ${props => props.theme.palette.neutral.default.four};
  }
`

const InfiniteConciergeDesktopPhoneNumber = styled.span`
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline;
  }
`

const InfiniteConciergeInfoButton = styled(Clickable)`
  display: inline-flex;
  color: inherit;
`

const INFINITE_BENEFITS: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'lounge_pass',
  'airport_transfer',
  'esim_package',
]

const INFINITE_BENEFIT_ICONS: Utils.PartialRecord<App.LoyaltyBenefitType, React.ReactElement> = {
  airport_transfer: <LineTaxiIcon />,
  esim_package: <LineMobileAndroidIcon />,
  hotel_upgrade: <LineRoomUpgradeIcon />,
  lounge_pass: <LineChairWithAirplaneIcon />,
}

function AccountMenuLoyaltyInfiniteDetailsTile({ onLinkClick, loyaltyAccountData }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const points = loyaltyAccountData.points
  const esimBenefit = useLoyaltyUserBenefitView('esim_package')?.benefit
  const esimGbAmount = getLoyaltyEsimGbAmount(esimBenefit)
  const membershipNumber = `${loyaltyAccountData.accountNumber}`
  const membershipLabel = intl.formatMessage(
    {
      id: 'EmBa2cCCza9gGbJVI4l1',
      defaultMessage: 'Membership {membershipNumber}',
      description: 'Loyalty membership number display in account menu element',
    },
    { membershipNumber },
  )
  const copyMembershipNumberLabel = intl.formatMessage({
    id: 'gkoIBvPTDxg5zeyp1z9S',
    defaultMessage: 'Copy membership number',
    description: 'Aria label for button to copy loyalty membership number',
  })
  const ptsLabel = intl.formatMessage({
    id: 'sUWfy3VsXfZr9Av3nmPz',
    defaultMessage: 'pts',
    description: 'Abbreviated label for loyalty points in account drawer',
  })
  const viewAllBenefitsLabel = intl.formatMessage({
    id: 'r-Nmxm4qxVPQMhzwjPnH',
    defaultMessage: 'View all benefits',
    description: 'Link text to view all Infinite benefits in account drawer',
  })
  const conciergeHeading = intl.formatMessage({
    id: 'mn04wVbCWRMt4sZfzylS',
    defaultMessage: 'Your dedicated concierge',
    description: 'Infinite concierge card heading in account drawer tile',
  })
  const callLabel = intl.formatMessage({
    id: '6gAJalaK7gPvSKsgR7PW',
    defaultMessage: 'Call',
    description: 'Call action label for Infinite concierge in account drawer',
  })
  const emailLabel = intl.formatMessage({
    id: 'z5rVH_0Vt7sD_7_DeCAm',
    defaultMessage: 'Email',
    description: 'Email action label for Infinite concierge in account drawer',
  })
  const conciergeDetails = useInfiniteConciergeDetails()
  const infiniteBenefitTitles: Utils.PartialRecord<App.LoyaltyBenefitType, string> = {
    airport_transfer: intl.formatMessage({
      id: '01ZZ-yFFqXTgBn6zKXPk',
      defaultMessage: 'Infinite chauffeur airport transfers',
      description: 'Benefit label for chauffeur-driven airport transfers for members of the Infinite loyalty tier. Infinite is the tier name and must stay unchanged; it does not mean unlimited transfers.',
    }),
    esim_package: esimGbAmount ? formatLoyaltyEsimInternationalDataHeading(intl, esimGbAmount) : undefined,
    hotel_upgrade: intl.formatMessage({
      id: 'AHRnY-MwG4sR5s8JQ3OZ',
      defaultMessage: 'Infinite room & package upgrades',
      description: 'Room and package upgrade benefit title in the account drawer for the named Infinite loyalty tier. Preserve Infinite as the tier name; this heading does not itself state an unlimited number of upgrades.',
    }),
    lounge_pass: intl.formatMessage({
      id: 'MED5JFlS5SnJq0KHIIrz',
      defaultMessage: 'Infinite airport lounge passes',
      description: 'Title for the airport-lounge-pass benefit of the Infinite loyalty membership tier in the account drawer. Infinite is the tier proper name, not a promise of an unlimited number of passes.',
    }),
  }
  const displayedBenefits = INFINITE_BENEFITS.filter(benefit => !!infiniteBenefitTitles[benefit])
  const handleNestedLinkClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])
  const handleConciergeInfoClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    showModal(<InfiniteConciergeDetailsModal />)
  }, [showModal])

  return <ProductPaletteProvider palette="tier-infinite">
    <Container
      to={`/${LOYALTY_LANDING_PAGE}`}
      onClick={onLinkClick}
      className="tier-infinite"
    >
      <VerticalSpacer gap={20}>
        <VerticalSpacer gap={8}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <LoyaltyInfiniteBrandLogo height={22} />
          </Group>
          <Group direction="vertical" gap={20}>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Caption variant="large">{membershipLabel}</Caption>
              <CopyToClipboardClickableIcon
                value={membershipNumber}
                ariaLabel={copyMembershipNumberLabel}
                stopPropagationOnCopy
              />
            </Group>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Heading variant="heading6">
                <FormatDecimal value={points} />
              </Heading>
              <Caption variant="medium" weight="normal">{ptsLabel}</Caption>
            </Group>
          </Group>
        </VerticalSpacer>

        <InfiniteBenefitList>
          {displayedBenefits.map((benefit) => (
            <React.Fragment key={benefit}>
              <InfiniteBenefitItem>
                {INFINITE_BENEFIT_ICONS[benefit]}
                <BodyText variant="small" weight="normal">
                  {infiniteBenefitTitles[benefit]}
                </BodyText>
              </InfiniteBenefitItem>
              <InfiniteBenefitDivider kind="primary" />
            </React.Fragment>
          ))}
          <TextLink size="small" weight="bold">
            {viewAllBenefitsLabel}
          </TextLink>
        </InfiniteBenefitList>

        {conciergeDetails && <InfiniteConciergeCard>
          <Group direction="vertical" gap={0} horizontalAlign="center">
            <BodyTextBlock
              variant="small"
              weight="bold"
              colour="static-white"
              endIcon={<InfiniteConciergeInfoButton
                onClick={handleConciergeInfoClick}
                aria-label={intl.formatMessage({
                  id: 'D_AKmSbI2JytKsV37zEF',
                  defaultMessage: 'View travel concierge details',
                  description: 'Aria label for the Infinite concierge details info button',
                })}
              >
                <LineInfoCircleIcon size={14} />
              </InfiniteConciergeInfoButton>}
            >
              {conciergeHeading}
            </BodyTextBlock>
            <BodyText variant="medium">{conciergeDetails.name}</BodyText>
          </Group>
          <PaletteSheet paletteType="inverse">
            <Group direction="horizontal" gap={8}>
              <InfiniteAssistChip
                variant="outline"
                size="medium"
                startIcon={<LinePhoneIcon />}
                href={conciergeDetails.phoneHref}
                onClick={handleNestedLinkClick}
              >
                {callLabel}<InfiniteConciergeDesktopPhoneNumber> {conciergeDetails.phoneNumber}</InfiniteConciergeDesktopPhoneNumber>
              </InfiniteAssistChip>
              <InfiniteAssistChip
                variant="outline"
                size="medium"
                startIcon={<LineEnvelopeAltIcon />}
                href={conciergeDetails.emailHref}
                onClick={handleNestedLinkClick}
              >
                {emailLabel}
              </InfiniteAssistChip>
            </Group>
          </PaletteSheet>
        </InfiniteConciergeCard>}
      </VerticalSpacer>
    </Container>
  </ProductPaletteProvider>
}

export default AccountMenuLoyaltyInfiniteDetailsTile
